import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  List,
  ListItem,
  IconButton,
  CircularProgress,
  Tooltip,
  Avatar,
  Badge,
  Fade,
  Collapse,
} from "@mui/material";
import {
  Email,
  CheckCircle,
  Archive,
  ContentCopy,
  ExpandMore,
  ExpandLess,
  Send,
  AccessTime,
  TrendingUp,
} from "@mui/icons-material";
import ActionsService from "../../services/actions.service";
import { showMessage } from "../../customhooks/useNotifier";

const getReasonDisplay = (reason) => {
  const reasonMap = {
    pricing_question: "Pricing inquiry",
    complex_question: "Complex question",
    recommendation_needed: "Needs recommendation",
    custom_request: "Custom request",
    product_inquiry: "Product inquiry",
    support_needed: "Support needed",
    other: "Other",
  };
  return reasonMap[reason] || "Other";
};

const EmailLeads = () => {
  const [emailLeads, setEmailLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [filter, setFilter] = useState("all");

  /* 🔥 PAGINATION STATE */
  const [page, setPage] = useState(1);
  const pageSize = 5;

  useEffect(() => {
    const fetchEmailLeads = async () => {
      setLoading(true);
      try {
        // Fetch from real API
        const response = await ActionsService.getEmailLeads();

        // Transform backend data to match frontend format
        const leads = (response || []).map((lead) => ({
          id: lead.id,
          userEmail: lead.leadEmail,
          userName: lead.leadName || lead.leadEmail,
          question: lead.question,
          context: lead.context || lead.question,
          reason: lead.reason || "other",
          reasonDisplay: getReasonDisplay(lead.reason),
          status: lead.status || "new",
          createdAt: new Date(lead.createdAt),
          agentId: lead.agentId,
        }));

        setEmailLeads(leads);
        console.log("📧 Fetched email leads from API:", leads.length);
      } catch (error) {
        console.error("❌ Failed to fetch email leads:", error);
        setEmailLeads([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEmailLeads();
  }, []);

  const handleExpand = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const handleMarkFollowedUp = async (id) => {
    try {
      await ActionsService.updateEmailLeadStatus(id, "followed_up");
      // Update UI optimistically
      setEmailLeads((prev) =>
        prev.map((lead) =>
          lead.id === id ? { ...lead, status: "followed_up" } : lead
        )
      );
    } catch (error) {
      console.error("Failed to update lead status:", error);
    }
  };

  const handleArchive = async (id) => {
    try {
      await ActionsService.updateEmailLeadStatus(id, "archived");
      // Update UI optimistically
      setEmailLeads((prev) => prev.filter((lead) => lead.id !== id));
    } catch (error) {
      console.error("Failed to archive lead:", error);
    }
  };

  const handleCopyEmail = (email) => {
    navigator.clipboard.writeText(email);
    showMessage("Email copied to clipboard", "success");
  };

  const handleEmailClick = (lead) => {
    const email = lead.userEmail;
    const subject = `Re: ${lead.question}`;
    const domain = email.split("@")[1].toLowerCase();

    const encodedSubject = encodeURIComponent(subject);
    const encodedTo = encodeURIComponent(email);

    // Gmail
    if (domain === "gmail.com") {
      window.open(
        `https://mail.google.com/mail/?view=cm&fs=1&to=${encodedTo}&su=${encodedSubject}`,
        "_blank"
      );
      return;
    }

    // Yahoo
    if (domain === "yahoo.com") {
      window.open(
        `https://compose.mail.yahoo.com/?to=${encodedTo}&subject=${encodedSubject}`,
        "_blank"
      );
      return;
    }

    // Outlook / Hotmail / Live
    if (
      domain === "outlook.com" ||
      domain === "hotmail.com" ||
      domain === "live.com"
    ) {
      window.open(
        `https://outlook.live.com/mail/0/deeplink/compose?to=${encodedTo}&subject=${encodedSubject}`,
        "_blank"
      );
      return;
    }

    // Default → mailto
    window.location.href = `mailto:${email}?subject=${encodedSubject}`;
  };

  /* FILTER */
  const filteredLeads =
    filter === "all"
      ? emailLeads
      : emailLeads.filter((lead) => lead.status === filter);

  /* SORT (newest first) */
  const sortedLeads = [...filteredLeads].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );

  /* PAGINATION */
  const totalLeads = sortedLeads.length;
  const totalPages = Math.ceil(totalLeads / pageSize);

  const paginatedLeads = sortedLeads.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const newLeadsCount = emailLeads.filter(
    (lead) => lead.status === "new"
  ).length;
  const followedUpCount = emailLeads.filter(
    (lead) => lead.status === "followed_up"
  ).length;

  const getReasonColor = (reason) => {
    const colorMap = {
      pricing_question: "#3b82f6",
      complex_question: "#f59e0b",
      recommendation_needed: "#8b5cf6",
      policy_question: "#6b7280",
    };
    return colorMap[reason] || "#6b7280";
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <Card
        sx={{
          height: "100%",
          borderRadius: 4,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          overflow: "hidden",
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress size={48} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 4,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
        border: "1px solid rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
        background: "white",
      }}
    >
      {/* Header with gradient */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          p: 3,
          pb: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backdropFilter: "blur(10px)",
                }}
              >
                <Email sx={{ fontSize: 24 }} />
              </Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, fontSize: "1.5rem" }}
              >
                Email Leads
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.9)", ml: 7 }}
            >
              {newLeadsCount > 0
                ? `${newLeadsCount} new lead${
                    newLeadsCount > 1 ? "s" : ""
                  } need${newLeadsCount > 1 ? "" : "s"} follow-up`
                : "All caught up! 🎉"}
            </Typography>
          </Box>
          {newLeadsCount > 0 && (
            <Badge
              badgeContent={newLeadsCount}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  minWidth: 24,
                  height: 24,
                },
              }}
            >
              <TrendingUp sx={{ fontSize: 28, opacity: 0.9 }} />
            </Badge>
          )}
        </Box>

        {/* Filter Chips */}
        <Box sx={{ display: "flex", gap: 1, ml: 7, flexWrap: "wrap" }}>
          <Chip
            label={`All (${emailLeads.length})`}
            onClick={() => setFilter("all")}
            variant={filter === "all" ? "filled" : "outlined"}
            sx={{
              background:
                filter === "all" ? "rgba(255, 255, 255, 0.25)" : "transparent",
              color: "white",
              borderColor: "rgba(255, 255, 255, 0.3)",
              fontWeight: filter === "all" ? 600 : 400,
              "&:hover": {
                background: "rgba(255, 255, 255, 0.15)",
              },
            }}
            size="small"
          />
          <Chip
            label={`New (${newLeadsCount})`}
            onClick={() => setFilter("new")}
            variant={filter === "new" ? "filled" : "outlined"}
            sx={{
              background:
                filter === "new" ? "rgba(255, 255, 255, 0.25)" : "transparent",
              color: "white",
              borderColor: "rgba(255, 255, 255, 0.3)",
              fontWeight: filter === "new" ? 600 : 400,
              "&:hover": {
                background: "rgba(255, 255, 255, 0.15)",
              },
            }}
            size="small"
          />
          <Chip
            label={`Followed Up (${followedUpCount})`}
            onClick={() => setFilter("followed_up")}
            variant={filter === "followed_up" ? "filled" : "outlined"}
            sx={{
              background:
                filter === "followed_up"
                  ? "rgba(255, 255, 255, 0.25)"
                  : "transparent",
              color: "white",
              borderColor: "rgba(255, 255, 255, 0.3)",
              fontWeight: filter === "followed_up" ? 600 : 400,
              "&:hover": {
                background: "rgba(255, 255, 255, 0.15)",
              },
            }}
            size="small"
          />
        </Box>
      </Box>

      <CardContent
        sx={{ flex: 1, display: "flex", flexDirection: "column", p: 0 }}
      >
        {paginatedLeads.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Email
              sx={{ fontSize: 64, color: "text.disabled", mb: 2, opacity: 0.3 }}
            />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No email leads
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filter !== "all"
                ? `No leads with status "${filter}"`
                : "All clear! No leads to follow up."}
            </Typography>
          </Box>
        ) : (
          <List sx={{ flex: 1, overflow: "auto", p: 2, pb: 2 }}>
            {paginatedLeads.map((lead, index) => (
              <Fade in key={lead.id} timeout={300 + index * 100}>
                <ListItem
                  sx={{
                    flexDirection: "column",
                    alignItems: "stretch",
                    bgcolor:
                      lead.status === "new"
                        ? "rgba(102, 126, 234, 0.05)"
                        : "transparent",
                    borderRadius: 3,
                    mb: 1.5,
                    px: 2.5,
                    py: 2,
                    border:
                      lead.status === "new"
                        ? "1px solid rgba(102, 126, 234, 0.15)"
                        : "1px solid rgba(0, 0, 0, 0.08)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      borderColor:
                        lead.status === "new"
                          ? "rgba(102, 126, 234, 0.3)"
                          : "rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                    {/* Avatar */}
                    <Avatar
                      sx={{
                        bgcolor: getReasonColor(lead.reason),
                        width: 48,
                        height: 48,
                        fontWeight: 600,
                        fontSize: "1rem",
                      }}
                    >
                      {getInitials(lead.userName)}
                    </Avatar>

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, mb: 0.5 }}
                          >
                            {lead.userName || lead.userEmail}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              mb: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                            }}
                          >
                            {lead.question}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.5,
                            ml: 1,
                          }}
                        >
                          <Tooltip title="View details">
                            <IconButton
                              size="small"
                              onClick={() => handleExpand(lead.id)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              {expandedItems.has(lead.id) ? (
                                <ExpandLess />
                              ) : (
                                <ExpandMore />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Copy email">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyEmail(lead.userEmail)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { bgcolor: "action.hover" },
                              }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label={lead.reasonDisplay}
                          size="small"
                          sx={{
                            bgcolor: `${getReasonColor(lead.reason)}15`,
                            color: getReasonColor(lead.reason),
                            fontWeight: 500,
                            height: 24,
                          }}
                        />
                        {lead.status === "new" && (
                          <Chip
                            label="New"
                            size="small"
                            color="primary"
                            sx={{ height: 24, fontWeight: 600 }}
                          />
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            ml: "auto",
                          }}
                        >
                          <AccessTime
                            sx={{ fontSize: 14, color: "text.secondary" }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {formatTimeAgo(lead.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Expanded Details */}

                  <Collapse
                    in={expandedItems.has(lead.id)}
                    timeout={300}
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, mb: 1, display: "block" }}
                      >
                        Full Context:
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2.5,
                          color: "text.secondary",
                          lineHeight: 1.6,
                        }}
                      >
                        {lead.context}
                      </Typography>

                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        {lead.status === "new" && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircle />}
                            onClick={() => handleMarkFollowedUp(lead.id)}
                            sx={{
                              background:
                                "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                              "&:hover": {
                                background:
                                  "linear-gradient(135deg, #059669 0%, #047857 100%)",
                              },
                              fontWeight: 600,
                              textTransform: "none",
                            }}
                          >
                            Mark as Followed Up
                          </Button>
                        )}

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Send />}
                          onClick={() => handleEmailClick(lead)}
                          sx={{ textTransform: "none", fontWeight: 500 }}
                        >
                          Send Email
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Archive />}
                          onClick={() => handleArchive(lead.id)}
                          sx={{ textTransform: "none", fontWeight: 500 }}
                        >
                          Archive
                        </Button>
                      </Box>
                    </Box>
                  </Collapse>

                  {/* <Slide
                    direction="down"
                    in={expandedItems.has(lead.id)}
                    timeout={300}
                  >
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, mb: 1, display: "block" }}
                      >
                        Full Context:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 2.5,
                          color: "text.secondary",
                          lineHeight: 1.6,
                        }}
                      >
                        {lead.context}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                        {lead.status === "new" && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<CheckCircle />}
                            onClick={() => handleMarkFollowedUp(lead.id)}
                            sx={{
                              background:
                                "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                              "&:hover": {
                                background:
                                  "linear-gradient(135deg, #059669 0%, #047857 100%)",
                              },
                              fontWeight: 600,
                              textTransform: "none",
                            }}
                          >
                            Mark as Followed Up
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Send />}
                          // href={`mailto:${
                          //   lead.userEmail
                          // }?subject=Re: ${encodeURIComponent(lead.question)}`}
                          onClick={() => handleEmailClick(lead)}
                          sx={{ textTransform: "none", fontWeight: 500 }}
                        >
                          Send Email
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Archive />}
                          onClick={() => handleArchive(lead.id)}
                          sx={{ textTransform: "none", fontWeight: 500 }}
                        >
                          Archive
                        </Button>
                      </Box>
                    </Box>
                  </Slide> */}
                </ListItem>
              </Fade>
            ))}
          </List>
        )}

        {/* 🔥 PAGINATION BUTTONS */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              pb: 2,
              mt: 2,
            }}
          >
            <Button
              variant="outlined"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>

            <Typography>
              Page {page} / {totalPages}
            </Typography>

            <Button
              variant="outlined"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EmailLeads;
